import { useCallback, useMemo, useState } from "react";
import {
  AlertIcon,
  CheckIcon,
  InfoIcon,
  CheckboxRow,
  DashButton,
  DataSurface,
  Field,
  Input,
  ModalCard,
  Note,
  Panel,
  SectionHead,
  Select,
  StatusPill,
  toneIcon,
  TableCard,
  Td,
  Thead,
  Tr,
} from "../../../shared/components";
import { useResource } from "../../../shared/hooks/useResource";
import {
  createPlan,
  formatPaise,
  getPayments,
  getPlans,
  getSubscription,
  getSubscriptions,
  grantTrial,
  startSubscription,
  type PaymentRow,
  type PlanRow,
  type SubscriptionDetail,
  type SubscriptionRow,
} from "../../../shared/api/billing";
import { ApiError } from "../../../shared/api/client";
import type { StaffAuth } from "../../../shared/api/teaching";
import type { BillingPeriod } from "../../../shared/types/prisma-enums";
import type { Lang } from "../../../shared/i18n";
import {
  BILLING_COPY,
  paymentStatusLabel,
  statusLabel,
  statusTone,
} from "./billingCopy";
import {
  collectedEver,
  collectedThisMonth,
  monthLabel,
  monthlyRevenue,
  outstanding,
  recurringRevenue,
  revenueByPlan,
  seriesArea,
  seriesPath,
  type RevenuePoint,
} from "./revenueModel";
import {
  describePlanFailure,
  planDraftIsValid,
  planFloorRemark,
  validatePlanDraft,
  PLAN_BLURB_MAX,
  PLAN_CODE_MAX,
  PLAN_NAME_MAX,
  type PlanDraft,
} from "./planModel";

interface BaseProps {
  lang?: Lang;
  auth?: StaffAuth;
}

/* ─────────────────────────────────── change 19 §3 · the money screen ── */

const TINT: Record<string, { bg: string; fg: string }> = {
  purple: { bg: "bg-purple-100", fg: "text-purple-600" },
  blue: { bg: "bg-blue-100", fg: "text-blue-600" },
  teal: { bg: "bg-teal-100", fg: "text-teal-600" },
  emerald: { bg: "bg-emerald-100", fg: "text-emerald-600" },
  rose: { bg: "bg-rose-100", fg: "text-rose-600" },
  amber: { bg: "bg-amber-100", fg: "text-amber-600" },
};

/**
 * One financial figure.
 *
 * 🔴 EVERY TILE HAS A NOTE AND THE NOTE IS A FACT. The six it replaces each
 * carried a hardcoded `▲ 0.0% vs last month` in green — a growth indicator that
 * showed growth on a dataset with no history, on a screen about money.
 */
function MoneyTile({
  tint,
  label,
  value,
  note,
  tone,
}: {
  tint: keyof typeof TINT;
  label: string;
  value: string;
  note: string;
  /** Omitted is neutral. A colour is only used where the direction is a fact. */
  tone?: "up" | "down";
}) {
  const c = TINT[tint];
  return (
    <div className="rounded-2xl border border-line bg-surface p-4 shadow-card flex flex-col justify-between">
      <div className={`mb-3 h-10 w-10 rounded-xl ${c.bg} ${c.fg} flex items-center justify-center shrink-0`}>
        <InfoIcon size={20} />
      </div>
      <div>
        <span className="text-dash-xs font-bold text-muted block">{label}</span>
        <span className="text-dash-xl font-extrabold text-ink tabular block my-1">{value}</span>
        <span
          className={`text-dash-xs font-bold block ${
            tone === "up" ? "text-emerald-600" : tone === "down" ? "text-rose-600" : "text-muted"
          }`}
        >
          {note}
        </span>
      </div>
    </div>
  );
}

/**
 * Month against month, in words.
 *
 * ⚠️ IT REFUSES A PERCENTAGE AGAINST ZERO. "∞% up" and "NaN%" are the two things
 * `(now - was) / was` produces on a platform's first month, and both are worse
 * than the sentence.
 */
function describeChange(now: number, was: number): string {
  if (was === 0) return now === 0 ? "Nothing collected last month either" : "First collections in this window";
  const pct = Math.round(((now - was) / was) * 1000) / 10;
  if (pct === 0) return "Level with last month";
  return `${pct > 0 ? "▲" : "▼"} ${String(Math.abs(pct))}% vs last month`;
}

/**
 * Twelve months of collections.
 *
 * ⚠️ IT DRAWS THE EMPTY CASE AS A FLAT LINE ON THE FLOOR RATHER THAN AS NOTHING.
 * A chart that disappears when there is no revenue is a chart an operator reads
 * as broken; a flat line at zero is the true answer and is visibly the true
 * answer.
 */
function RevenueChart({ series, now }: { series: readonly RevenuePoint[]; now: Date }) {
  const peak = Math.max(...series.map((p) => p.paise), 0);
  return (
    <div>
      <div className="relative h-44 w-full">
        <svg
          className="w-full h-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          role="img"
          aria-label={`Monthly collections, peaking at ${formatPaise(peak)}`}
        >
          <defs>
            <linearGradient id="collectedGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-primary)" stopOpacity="0.28" />
              <stop offset="100%" stopColor="var(--color-primary)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {[25, 50, 75].map((y) => (
            <line key={y} x1="0" y1={y} x2="100" y2={y} stroke="var(--color-line)" strokeWidth="0.4" />
          ))}
          <path d={seriesArea(series)} fill="url(#collectedGrad)" />
          <path
            d={seriesPath(series)}
            fill="none"
            stroke="var(--color-primary)"
            strokeWidth="1.2"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </div>
      {/* ⚠️ Every fourth label, so twelve months fit at 1366px without stacking. */}
      <div className="mt-2 flex justify-between text-[11px] font-bold text-muted">
        {series.map((point, i) => (
          <span key={point.month}>{i % 3 === 0 ? monthLabel(point.month, now) : ""}</span>
        ))}
      </div>
    </div>
  );
}

export function SubscriptionsScreen({ lang = "en", auth }: BaseProps) {
  const t = BILLING_COPY[lang];
  const [activeTab, setActiveTab] = useState<
    "overview" | "subscriptions" | "plans" | "transactions" | "invoices" | "payment-methods"
  >("overview");

  // Filter States
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [planFilter, setPlanFilter] = useState("ALL");
  const [cycleFilter, setCycleFilter] = useState("ALL");
  const [selectedSchoolId, setSelectedSchoolId] = useState<string | null>(null);

  const closeSchool = useCallback(() => setSelectedSchoolId(null), []);

  // Fetch Real Data from Backend APIs
  const subsResource = useResource<SubscriptionRow[]>(
    (signal) => getSubscriptions({ ...auth, signal }),
    ["subscriptions"],
    { isEmpty: (rows) => rows.length === 0 }
  );

  const plansResource = useResource<PlanRow[]>(
    (signal) => getPlans({ ...auth, signal }),
    ["plans"]
  );

  const paymentsResource = useResource<PaymentRow[]>(
    (signal) => getPayments({ ...auth, signal }),
    ["payments"],
    { isEmpty: (rows) => rows.length === 0 }
  );

  const realSubs = subsResource.data ?? [];
  const realPlans = plansResource.data ?? [];
  const realPayments = paymentsResource.data ?? [];

  // Real Metrics Aggregation — Strict 0 when no data
  /* ⚠️ ONE CLOCK PER MOUNT, NOT ONE PER `useMemo` RUN. A `new Date()` inside the
     memo would make the dependency array meaningless and could put a payment in
     a different month between two renders of the same screen. */
  const now = useMemo(() => new Date(), []);

  const metrics = useMemo(() => {
    const totalCustomers = realSubs.length;
    const activeSubs = realSubs.filter((s) => s.status === "ACTIVE").length;

    /* ⚠️ `nowMs`, NOT `now`. This shadowed the `Date` above it, so every revenue
       call in this memo silently received a millisecond number where a `Date`
       was expected — one line apart, both named `now`, both plausible. */
    const nowMs = now.getTime();
    const SEVEN_DAYS_MS = 7 * 86_400_000;
    const expiringSoon = realSubs.filter((s) => {
      if (s.status === "TRIAL") return true;
      if (s.currentPeriodEnd) {
        const diff = new Date(s.currentPeriodEnd).getTime() - nowMs;
        return diff >= 0 && diff <= SEVEN_DAYS_MS;
      }
      return false;
    }).length;

    const overduePayments = realSubs.filter(
      (s) => s.status === "PAST_DUE" || s.status === "GRACE"
    ).length;

    /*
     * 🔴 CHANGE 19 §3 — EVERY FIGURE BELOW COMES OUT OF `revenueModel.ts`, AND
     * THE THREE IT REPLACES WERE EACH WRONG IN A FLATTERING DIRECTION:
     *
     *   · MRR branched on YEARLY and swept everything else into the monthly
     *     bucket, so a QUARTERLY plan counted three times over;
     *   · "Total Revenue (This Month)" summed every payment ever recorded;
     *   · the revenue curve was two hardcoded SVG paths that rose in every
     *     dataset, under an axis labelled Day 1 … Day 30.
     */
    const recurring = recurringRevenue(realSubs);
    const owed = outstanding(realSubs);
    const series = monthlyRevenue(realPayments, now, 12);
    const thisMonth = collectedThisMonth(realPayments, now);
    /* Last month, for a comparison that is arithmetic rather than a green arrow
       with 0.0% printed beside it. */
    const lastMonth = series.at(-2)?.paise ?? 0;

    return {
      totalCustomers,
      activeSubs,
      expiringSoon,
      overduePayments,
      recurring,
      owed,
      series,
      thisMonth,
      lastMonth,
      collectedEverPaise: collectedEver(realPayments),
    };
  }, [realSubs, realPayments, now]);

  // Real Subscriptions By Plan Breakdown
  const planBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    const total = realSubs.length;

    if (total === 0) return [];

    realSubs.forEach((s) => {
      const planName = s.plan?.name ?? "Unassigned Plan";
      counts[planName] = (counts[planName] || 0) + 1;
    });

    const colors = ["bg-purple-600", "bg-blue-500", "bg-teal-500", "bg-amber-500", "bg-pink-500"];

    return Object.entries(counts).map(([name, count], i) => ({
      name,
      count,
      percent: `${((count / total) * 100).toFixed(1)}%`,
      color: colors[i % colors.length],
    }));
  }, [realSubs]);

  // Real Subscriptions Status Breakdown
  const statusBreakdown = useMemo(() => {
    let active = 0;
    let expiring = 0;
    let pastDue = 0;
    let cancelled = 0;
    const total = realSubs.length;

    realSubs.forEach((s) => {
      if (s.status === "ACTIVE") active++;
      else if (s.status === "TRIAL") expiring++;
      else if (s.status === "PAST_DUE" || s.status === "GRACE") pastDue++;
      else if (s.status === "CANCELLED") cancelled++;
    });

    if (total === 0) {
      return {
        total: 0,
        active: { count: 0, percent: "0%" },
        expiring: { count: 0, percent: "0%" },
        pastDue: { count: 0, percent: "0%" },
        cancelled: { count: 0, percent: "0%" },
      };
    }

    return {
      total,
      active: { count: active, percent: `${((active / total) * 100).toFixed(1)}%` },
      expiring: { count: expiring, percent: `${((expiring / total) * 100).toFixed(1)}%` },
      pastDue: { count: pastDue, percent: `${((pastDue / total) * 100).toFixed(1)}%` },
      cancelled: { count: cancelled, percent: `${((cancelled / total) * 100).toFixed(1)}%` },
    };
  }, [realSubs]);

  /* 🔴 NORMALISED PER MONTH — see `revenueByPlan`. The panel this feeds used to
     compare a yearly charge with a monthly one directly. */
  const planRevenue = useMemo(() => revenueByPlan(realSubs), [realSubs]);

  // Real Payment Summary Breakdown
  const paymentSummary = useMemo(() => {
    let paidPaise = 0;
    let pendingPaise = 0;
    let failedPaise = 0;
    let refundedPaise = 0;

    realPayments.forEach((p) => {
      if (p.status === "PAID") paidPaise += p.amountPaise;
      else if (p.status === "PENDING") pendingPaise += p.amountPaise;
      else if (p.status === "FAILED") failedPaise += p.amountPaise;
      else if (p.status === "REFUNDED") refundedPaise += p.amountPaise;
    });

    const totalPaise = paidPaise + pendingPaise + failedPaise + refundedPaise;
    const div = totalPaise || 1;

    return {
      paid: { paise: paidPaise, percent: totalPaise === 0 ? "0%" : `${((paidPaise / div) * 100).toFixed(1)}%` },
      pending: { paise: pendingPaise, percent: totalPaise === 0 ? "0%" : `${((pendingPaise / div) * 100).toFixed(1)}%` },
      failed: { paise: failedPaise, percent: totalPaise === 0 ? "0%" : `${((failedPaise / div) * 100).toFixed(1)}%` },
      refunded: { paise: refundedPaise, percent: totalPaise === 0 ? "0%" : `${((refundedPaise / div) * 100).toFixed(1)}%` },
    };
  }, [realPayments]);

  // Filtered Subscriptions Table Rows
  const filteredSubscriptions = useMemo(() => {
    return realSubs.filter((sub) => {
      const matchSearch =
        sub.schoolName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (sub.plan?.name ?? "").toLowerCase().includes(searchQuery.toLowerCase());
      const matchStatus = statusFilter === "ALL" || sub.status === statusFilter;
      const matchPlan = planFilter === "ALL" || sub.plan?.name === planFilter;
      const matchCycle =
        cycleFilter === "ALL" ||
        (cycleFilter === "Annual" && sub.plan?.period === "YEARLY") ||
        (cycleFilter === "Monthly" && sub.plan?.period === "MONTHLY");

      return matchSearch && matchStatus && matchPlan && matchCycle;
    });
  }, [realSubs, searchQuery, statusFilter, planFilter, cycleFilter]);

  const resetFilters = () => {
    setSearchQuery("");
    setStatusFilter("ALL");
    setPlanFilter("ALL");
    setCycleFilter("ALL");
  };

  return (
    <section className="font-dash text-dash-sm text-ink pb-12 space-y-6">
      {/* 1. Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-dash text-dash-2xl font-extrabold text-ink">Subscriptions & Billing</h1>
          <p className="mt-1 font-dash text-dash-sm text-muted">
            Manage subscription plans, active subscriptions, payments and invoices.
          </p>
        </div>
        {/*
          🔴 "+ CREATE PLAN" IS GONE FROM THE PAGE HEADER. It never created a
          plan — it switched to the Plans tab, where the form used to sit — so
          the plus sign promised an action that landed the operator in front of
          six empty fields they had not asked for. The tab's own "Create a plan"
          is the one control that does the thing its label says.
        */}
      </div>

      {/* 2. Sub-Navigation Tabs */}
      <div className="flex items-center gap-6 border-b border-line pb-0.5 overflow-x-auto">
        <button
          type="button"
          onClick={() => setActiveTab("overview")}
          className={`font-extrabold text-dash-sm pb-3 border-b-2 cursor-pointer transition-colors whitespace-nowrap ${
            activeTab === "overview" ? "text-primary border-primary" : "text-muted border-transparent hover:text-ink"
          }`}
        >
          Overview
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("subscriptions")}
          className={`font-extrabold text-dash-sm pb-3 border-b-2 cursor-pointer transition-colors whitespace-nowrap ${
            activeTab === "subscriptions" ? "text-primary border-primary" : "text-muted border-transparent hover:text-ink"
          }`}
        >
          Subscriptions ({realSubs.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("plans")}
          className={`font-extrabold text-dash-sm pb-3 border-b-2 cursor-pointer transition-colors whitespace-nowrap ${
            activeTab === "plans" ? "text-primary border-primary" : "text-muted border-transparent hover:text-ink"
          }`}
        >
          Plans ({realPlans.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("transactions")}
          className={`font-extrabold text-dash-sm pb-3 border-b-2 cursor-pointer transition-colors whitespace-nowrap ${
            activeTab === "transactions" ? "text-primary border-primary" : "text-muted border-transparent hover:text-ink"
          }`}
        >
          Transactions ({realPayments.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("invoices")}
          className={`font-extrabold text-dash-sm pb-3 border-b-2 cursor-pointer transition-colors whitespace-nowrap ${
            activeTab === "invoices" ? "text-primary border-primary" : "text-muted border-transparent hover:text-ink"
          }`}
        >
          Invoices
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("payment-methods")}
          className={`font-extrabold text-dash-sm pb-3 border-b-2 cursor-pointer transition-colors whitespace-nowrap ${
            activeTab === "payment-methods" ? "text-primary border-primary" : "text-muted border-transparent hover:text-ink"
          }`}
        >
          Payment Methods
        </button>
      </div>

      {/* Overview Tab Content */}
      {activeTab === "overview" && (
        <>
          {/* 3. Top 6 Metric Cards Grid (Strict Real Database Values) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            <MoneyTile
              tint="purple"
              label="Recurring revenue, per month"
              /* 🔴 NOT "REVENUE". This is what live subscriptions come to at
                 today's enrolment — a forecast that moves whenever a child is
                 enrolled. The money is the tile beside it. */
              value={formatPaise(metrics.recurring.mrrPaise)}
              note={`${formatPaise(metrics.recurring.arrPaise)} a year at this rate`}
            />

            <MoneyTile
              tint="blue"
              label="Collected this month"
              value={formatPaise(metrics.thisMonth)}
              /* 🔴 A REAL COMPARISON. This tile carried "▲ 0.0% vs last month"
                 in green, on every dataset, forever. */
              note={describeChange(metrics.thisMonth, metrics.lastMonth)}
              tone={metrics.thisMonth >= metrics.lastMonth ? "up" : "down"}
            />

            <MoneyTile
              tint="teal"
              label="Collected, all time"
              value={formatPaise(metrics.collectedEverPaise)}
              note={`${String(metrics.recurring.payingSchools)} schools paying now`}
            />

            <MoneyTile
              tint="emerald"
              label="Per school, per month"
              /* `null` is "nobody is paying", which is not ₹0 per school. */
              value={
                metrics.recurring.arpuPaise === null
                  ? "—"
                  : formatPaise(metrics.recurring.arpuPaise)
              }
              note={
                metrics.recurring.perChildPaise === null
                  ? "No paying schools yet"
                  : `${formatPaise(metrics.recurring.perChildPaise)} per child`
              }
            />

            <MoneyTile
              tint="rose"
              label="Owed and unpaid"
              value={formatPaise(metrics.owed.paise)}
              note={
                metrics.owed.schools === 0
                  ? "Every school is current"
                  : `${String(metrics.owed.schools)} school${metrics.owed.schools === 1 ? "" : "s"} past due or in grace`
              }
              tone={metrics.owed.schools === 0 ? "up" : "down"}
            />

            <MoneyTile
              tint="amber"
              label="Renewing within 7 days"
              value={String(metrics.expiringSoon)}
              note={`${String(metrics.activeSubs)} active of ${String(metrics.totalCustomers)} schools`}
            />
          </div>

          {/* 4. Middle Visual Analytics Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
            {/* 🔴 CARD 1 — A REAL CHART. What stood here was two `<path d="M0,90
                Q70,75 …">` literals rendered whenever total revenue was above
                zero, under an axis labelled Day 1 … Day 30 and a period selector
                wired to nothing. It rose in every deployment and every month,
                including months where collections fell. `seriesPath` takes the
                ledger and returns the geometry, so a flat month draws flat. */}
            <div className="lg:col-span-6 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col justify-between space-y-4">
              <div>
                <h2 className="text-dash-md font-extrabold text-ink">What was collected</h2>
                <p className="text-dash-xs text-muted mt-0.5">
                  Twelve months of paid payments. Pending and failed payments are not
                  revenue and are not counted.
                </p>
              </div>

              <RevenueChart series={metrics.series} now={now} />

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-line text-dash-xs">
                <div>
                  <span className="text-muted font-semibold block">This month</span>
                  <span className="font-extrabold text-dash-sm text-ink tabular block mt-0.5">
                    {formatPaise(metrics.thisMonth)}
                  </span>
                </div>
                <div>
                  <span className="text-muted font-semibold block">Last month</span>
                  <span className="font-extrabold text-dash-sm text-ink tabular block mt-0.5">
                    {formatPaise(metrics.lastMonth)}
                  </span>
                </div>
                <div>
                  <span className="text-muted font-semibold block">Best month</span>
                  <span className="font-extrabold text-dash-sm text-ink tabular block mt-0.5">
                    {formatPaise(Math.max(0, ...metrics.series.map((p) => p.paise)))}
                  </span>
                </div>
                <div>
                  <span className="text-muted font-semibold block">Twelve-month total</span>
                  <span className="font-extrabold text-dash-sm text-ink tabular block mt-0.5">
                    {formatPaise(metrics.series.reduce((sum, p) => sum + p.paise, 0))}
                  </span>
                </div>
              </div>
            </div>

            {/* Card 2: Subscriptions by Plan (Strict Real DB) */}
            <div className="lg:col-span-3 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col justify-between space-y-4">
              <h2 className="text-dash-md font-extrabold text-ink">Subscriptions by Plan</h2>

              <div className="flex items-center gap-4 py-2">
                <div className="relative h-32 w-32 shrink-0 flex items-center justify-center">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#e2e8f0"
                      strokeWidth="3.8"
                    />
                    {planBreakdown.length > 0 && (
                      <path
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="#8b5cf6"
                        strokeWidth="3.8"
                        strokeDasharray="100, 100"
                      />
                    )}
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                    <span className="text-dash-md font-extrabold text-ink leading-tight">
                      {realSubs.length}
                    </span>
                    <span className="text-[10px] font-bold text-muted uppercase">Total</span>
                  </div>
                </div>

                <div className="space-y-1.5 text-[11px] font-bold text-ink min-w-0 flex-1">
                  {planBreakdown.length === 0 ? (
                    <span className="text-dash-xs font-bold text-muted block py-4 text-center">No subscriptions in database</span>
                  ) : (
                    planBreakdown.map((item) => (
                      <div key={item.name} className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-muted truncate">
                          <span className={`h-2.5 w-2.5 rounded-full ${item.color} shrink-0`} />
                          <span className="truncate">{item.name}</span>
                        </span>
                        <span className="tabular font-extrabold shrink-0">
                          {item.count} ({item.percent})
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Card 3: Subscriptions Status (Strict Real DB) */}
            <div className="lg:col-span-3 rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card flex flex-col justify-between space-y-4">
              <h2 className="text-dash-md font-extrabold text-ink">Subscriptions Status</h2>

              <div className="flex items-center gap-4 py-2">
                <div className="relative h-32 w-32 shrink-0 flex items-center justify-center">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#e2e8f0"
                      strokeWidth="3.8"
                    />
                    {statusBreakdown.total > 0 && (
                      <path
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        stroke="#10b981"
                        strokeWidth="3.8"
                        strokeDasharray={`${statusBreakdown.active.percent.replace('%', '')}, 100`}
                      />
                    )}
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                    <span className="text-dash-md font-extrabold text-ink leading-tight">
                      {statusBreakdown.total}
                    </span>
                    <span className="text-[10px] font-bold text-muted uppercase">Total</span>
                  </div>
                </div>

                <div className="space-y-2 text-[11px] font-bold text-ink min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-muted truncate">
                      <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 shrink-0" />
                      <span>Active</span>
                    </span>
                    <span className="tabular font-extrabold shrink-0">
                      {statusBreakdown.active.count} ({statusBreakdown.active.percent})
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-muted truncate">
                      <span className="h-2.5 w-2.5 rounded-full bg-amber-500 shrink-0" />
                      <span>Expiring Soon</span>
                    </span>
                    <span className="tabular font-extrabold shrink-0">
                      {statusBreakdown.expiring.count} ({statusBreakdown.expiring.percent})
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-muted truncate">
                      <span className="h-2.5 w-2.5 rounded-full bg-rose-500 shrink-0" />
                      <span>Past Due</span>
                    </span>
                    <span className="tabular font-extrabold shrink-0">
                      {statusBreakdown.pastDue.count} ({statusBreakdown.pastDue.percent})
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-muted truncate">
                      <span className="h-2.5 w-2.5 rounded-full bg-slate-400 shrink-0" />
                      <span>Cancelled</span>
                    </span>
                    <span className="tabular font-extrabold shrink-0">
                      {statusBreakdown.cancelled.count} ({statusBreakdown.cancelled.percent})
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 5. Bottom Split Section */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            {/* Left Side: Recent Subscriptions Table (Strict Live DB) */}
            <div className="lg:col-span-8 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3 bg-surface p-4 rounded-2xl border border-line shadow-card">
                <div className="relative flex-1 min-w-[220px]">
                  <input
                    type="search"
                    placeholder="Search by institution name or plan..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full rounded-xl border border-line bg-white px-3 py-2 pl-9 text-dash-xs text-ink focus:border-primary focus:outline-none transition-colors"
                  />
                  <svg className="w-4 h-4 text-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>

                <div className="flex flex-wrap items-center gap-2 text-dash-xs">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="rounded-xl border border-line bg-white px-3 py-2 font-bold text-ink focus:border-primary focus:outline-none cursor-pointer"
                  >
                    <option value="ALL">All Status</option>
                    <option value="ACTIVE">Active</option>
                    <option value="TRIAL">Expiring Soon / Trial</option>
                    <option value="PAST_DUE">Past Due</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>

                  <select
                    value={cycleFilter}
                    onChange={(e) => setCycleFilter(e.target.value)}
                    className="rounded-xl border border-line bg-white px-3 py-2 font-bold text-ink focus:border-primary focus:outline-none cursor-pointer"
                  >
                    <option value="ALL">All Cycles</option>
                    <option value="Annual">Annual</option>
                    <option value="Monthly">Monthly</option>
                  </select>

                  <button
                    type="button"
                    onClick={resetFilters}
                    className="rounded-xl border border-line bg-white px-3 py-2 font-bold text-ink hover:bg-canvas transition-colors cursor-pointer"
                  >
                    Reset
                  </button>
                </div>
              </div>

              {/* Data Surface + Table */}
              <DataSurface
                state={subsResource.state}
                skeletonRows={5}
                emptyMessage={t.noSchools}
                errorMessage={t.loadFailed}
                onRetry={subsResource.retry}
              >
                <div className="rounded-2xl border border-line bg-surface shadow-card overflow-hidden">
                  <div className="px-6 py-4 border-b border-line">
                    <h2 className="text-dash-md font-extrabold text-ink">Recent Subscriptions</h2>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-dash-xs">
                      <thead>
                        <tr className="border-b border-line bg-gray-50/70 text-muted font-extrabold uppercase tracking-wider text-[11px]">
                          <th className="p-4 px-6">Institution</th>
                          <th className="p-4">Plan</th>
                          <th className="p-4">Billing Cycle</th>
                          <th className="p-4">Amount</th>
                          <th className="p-4">Status</th>
                          <th className="p-4">Payment Status</th>
                          <th className="p-4">Next Billing Date</th>
                          <th className="p-4 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-line/60">
                        {filteredSubscriptions.length === 0 ? (
                          <tr>
                            <td colSpan={8} className="p-8 text-center text-muted font-bold">
                              No subscription records found in database.
                            </td>
                          </tr>
                        ) : (
                          filteredSubscriptions.map((row) => {
                            const planName = row.plan?.name ?? t.noPlan;
                            const cycle = row.plan?.period === "YEARLY" ? "Annual" : "Monthly";
                            const amountText = row.netChargePaise !== null
                              ? formatPaise(row.netChargePaise, row.plan?.currency ?? "INR")
                              : "—";
                            const nextBilling = row.currentPeriodEnd
                              ? new Date(row.currentPeriodEnd).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                              : "—";

                            return (
                              <tr key={row.schoolId} className="hover:bg-gray-50/50 transition-colors">
                                <td className="p-4 px-6">
                                  <div className="flex items-center gap-3">
                                    <div className="h-9 w-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-extrabold shrink-0 text-dash-xs">
                                      {row.schoolName.charAt(0)}
                                    </div>
                                    <div>
                                      <span className="font-extrabold text-ink text-dash-sm block">{row.schoolName}</span>
                                      <span className="text-[11px] text-muted block">{row.seats} seats</span>
                                    </div>
                                  </div>
                                </td>
                                <td className="p-4 font-bold text-ink">{planName}</td>
                                <td className="p-4 font-semibold text-muted">{cycle}</td>
                                <td className="p-4 font-extrabold text-ink tabular">{amountText}</td>
                                <td className="p-4">
                                  <StatusPill
                                    tone={statusTone(row.status)}
                                    icon={toneIcon(statusTone(row.status))}
                                  >
                                    {statusLabel(row.status, lang)}
                                  </StatusPill>
                                </td>
                                <td className="p-4">
                                  {row.status === "ACTIVE" ? (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                      Paid
                                    </span>
                                  ) : row.status === "PAST_DUE" ? (
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
                                      Failed
                                    </span>
                                  ) : (
                                    <span className="text-muted font-bold">—</span>
                                  )}
                                </td>
                                <td className="p-4 font-semibold text-muted">{nextBilling}</td>
                                <td className="p-4 text-center">
                                  <button
                                    type="button"
                                    onClick={() => setSelectedSchoolId(row.schoolId)}
                                    className="text-muted hover:text-ink font-bold p-1 rounded-lg hover:bg-canvas transition-colors cursor-pointer"
                                  >
                                    ⋮
                                  </button>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="p-4 px-6 border-t border-line flex flex-col sm:flex-row items-center justify-between gap-4 text-dash-xs text-muted">
                    <span>Showing {filteredSubscriptions.length} of {realSubs.length} subscriptions</span>
                  </div>
                </div>
              </DataSurface>
            </div>

            {/* Right Side: Revenue & Payment Breakdown Cards */}
            <div className="lg:col-span-4 space-y-6">
              {/* 🔴 CARD 1 — BY PLAN, NORMALISED TO A MONTH. What stood here was
                  "Revenue by Billing Cycle": it added a yearly charge to a
                  monthly one and drew both as a share of the sum, so one annual
                  school looked like twelve monthly ones, and a plan's share moved
                  when a school switched period without paying a rupee more. It
                  also had a period selector wired to nothing. */}
              <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card space-y-4">
                <div>
                  <h3 className="text-dash-md font-extrabold text-ink">Recurring revenue by plan</h3>
                  <p className="text-dash-xs text-muted mt-0.5">
                    Every plan brought to a monthly figure, so an annual school and a
                    monthly one can be compared.
                  </p>
                </div>

                <div className="space-y-3 pt-1">
                  {planRevenue.length === 0 ? (
                    <p className="text-dash-xs text-muted">No school is on a paid plan yet.</p>
                  ) : (
                    planRevenue.map((row) => (
                      <div key={row.name}>
                        <div className="flex items-center justify-between text-dash-xs font-bold mb-1">
                          <span className="text-ink">
                            {row.name}{" "}
                            <span className="text-muted font-normal">
                              · {String(row.schools)} school{row.schools === 1 ? "" : "s"}
                            </span>
                          </span>
                          <span className="text-ink tabular">
                            {formatPaise(row.mrrPaise)}{" "}
                            <span className="text-muted font-normal">({String(row.share)}%)</span>
                          </span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-primary"
                            style={{ width: `${String(row.share)}%` }}
                          />
                        </div>
                      </div>
                    ))
                  )}

                  {/* 🔴 THE OMISSION MADE VISIBLE. A plan on a period the model
                      cannot read is excluded from every figure above; without
                      this line it would simply be missing. */}
                  {metrics.recurring.unreadable > 0 ? (
                    <p className="text-dash-xs font-bold text-rose-600">
                      {String(metrics.recurring.unreadable)} subscription
                      {metrics.recurring.unreadable === 1 ? " is" : "s are"} on a billing
                      period this screen cannot read, and {metrics.recurring.unreadable === 1 ? "is" : "are"} not counted above.
                    </p>
                  ) : null}
                </div>
              </div>

              {/* Card 2: Payment Summary */}
              <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-dash-md font-extrabold text-ink">Payment Summary</h3>
                  <select className="rounded-xl border border-line bg-white px-2.5 py-1 text-dash-xs font-bold text-ink cursor-pointer">
                    <option>This Month</option>
                    <option>This Year</option>
                  </select>
                </div>

                <div className="space-y-3 pt-1">
                  <div>
                    <div className="flex items-center justify-between text-dash-xs font-bold mb-1">
                      <span className="text-ink flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                        <span>Paid</span>
                      </span>
                      <span className="text-ink">
                        {formatPaise(paymentSummary.paid.paise)}{" "}
                        <span className="text-muted font-normal">({paymentSummary.paid.percent})</span>
                      </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: paymentSummary.paid.percent }} />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-dash-xs font-bold mb-1">
                      <span className="text-ink flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                        <span>Pending</span>
                      </span>
                      <span className="text-ink">
                        {formatPaise(paymentSummary.pending.paise)}{" "}
                        <span className="text-muted font-normal">({paymentSummary.pending.percent})</span>
                      </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full bg-amber-500 rounded-full" style={{ width: paymentSummary.pending.percent }} />
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-dash-xs font-bold mb-1">
                      <span className="text-ink flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full bg-rose-500" />
                        <span>Failed</span>
                      </span>
                      <span className="text-ink">
                        {formatPaise(paymentSummary.failed.paise)}{" "}
                        <span className="text-muted font-normal">({paymentSummary.failed.percent})</span>
                      </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
                      <div className="h-full bg-rose-500 rounded-full" style={{ width: paymentSummary.failed.percent }} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3: Quick Actions */}
              <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card space-y-4">
                <h3 className="text-dash-md font-extrabold text-ink">Quick Actions</h3>

                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setActiveTab("plans")}
                    className="w-full flex items-center justify-between p-3 rounded-xl border border-line bg-white hover:bg-canvas hover:border-primary/40 transition-colors text-dash-xs font-bold text-ink cursor-pointer"
                  >
                    <span className="flex items-center gap-2.5">
                      <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                      </svg>
                      <span>Create New Subscription Plan</span>
                    </span>
                    <span className="text-muted">›</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setActiveTab("transactions")}
                    className="w-full flex items-center justify-between p-3 rounded-xl border border-line bg-white hover:bg-canvas hover:border-primary/40 transition-colors text-dash-xs font-bold text-ink cursor-pointer"
                  >
                    <span className="flex items-center gap-2.5">
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <span>View All Transactions</span>
                    </span>
                    <span className="text-muted">›</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setActiveTab("invoices")}
                    className="w-full flex items-center justify-between p-3 rounded-xl border border-line bg-white hover:bg-canvas hover:border-primary/40 transition-colors text-dash-xs font-bold text-ink cursor-pointer"
                  >
                    <span className="flex items-center gap-2.5">
                      <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                      <span>Generate Invoice</span>
                    </span>
                    <span className="text-muted">›</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Subscriptions Tab Content */}
      {activeTab === "subscriptions" && (
        <DataSurface
          state={subsResource.state}
          skeletonRows={6}
          emptyMessage={t.noSchools}
          errorMessage={t.loadFailed}
          onRetry={subsResource.retry}
        >
          <TableCard>
            <table className="w-full border-collapse">
              <Thead headers={[t.school, t.plan, t.seats, t.discount, t.charge, t.renews, t.status, ""]} />
              <tbody>
                {realSubs.map((r) => (
                  <Tr key={r.schoolId}>
                    <Td bold>{r.schoolName}</Td>
                    <Td soft>{r.plan?.name ?? t.noPlan}</Td>
                    <Td num>{r.seats}</Td>
                    <Td soft>
                      {r.couponOff === null || r.couponCode === null
                        ? "—"
                        : t.discountOff(r.couponOff, r.couponCode)}
                    </Td>
                    <Td num>
                      {r.netChargePaise === null ? (
                        "—"
                      ) : (
                        formatPaise(r.netChargePaise, r.plan?.currency ?? "INR")
                      )}
                    </Td>
                    <Td soft>
                      {r.status === "TRIAL"
                        ? r.trialEndsAt === null
                          ? t.trialNoEnd
                          : t.trialEndsOn(new Date(r.trialEndsAt).toLocaleDateString())
                        : r.currentPeriodEnd === null
                        ? "—"
                        : new Date(r.currentPeriodEnd).toLocaleDateString()}
                    </Td>
                    <Td>
                      <StatusPill
                        tone={r.status === "ACTIVE" ? "success" : "neutral"}
                        icon={toneIcon(r.status === "ACTIVE" ? "success" : "neutral")}
                      >
                        {statusLabel(r.status, lang)}
                      </StatusPill>
                    </Td>
                    <Td>
                      <DashButton variant="ghost" onClick={() => setSelectedSchoolId(r.schoolId)}>
                        {t.open}
                      </DashButton>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </table>
          </TableCard>
        </DataSurface>
      )}

      {/* Plans Tab Content */}
      {activeTab === "plans" && (
        <PlanCatalogue lang={lang} auth={auth} plans={plansResource} />
      )}

      {/* Transactions / Payments Tab Content */}
      {(activeTab === "transactions" || activeTab === "invoices") && (
        <PaymentsPanel lang={lang} auth={auth} />
      )}

      {/*
        🔴 THE SCHOOL'S SUBSCRIPTION IS A DIALOG, NOT A SECOND PANEL BELOW THE
        TABLE. "Open" used to append `SchoolSubscriptionPanel` under the roster,
        which meant the operator clicked a row and — on any screen showing more
        than a handful of schools — nothing visibly happened, because the thing
        they opened was rendered a scroll or two below the fold. Worse, the panel
        and the row it belongs to could not be seen together, so "Move to this
        plan" was pressed against a school name the reader had to scroll back up
        to check.
      */}
      {selectedSchoolId !== null && (
        <ModalCard label={t.schoolDialog} onClose={closeSchool}>
          <SchoolSubscriptionPanel
            schoolId={selectedSchoolId}
            plans={realPlans}
            lang={lang}
            auth={auth}
            onChanged={subsResource.retry}
            onClose={closeSchool}
          />
        </ModalCard>
      )}
    </section>
  );
}

/* ─────────────────────────────────────────── Sub-Components ── */

function SchoolSubscriptionPanel({
  schoolId,
  plans,
  lang,
  auth,
  onChanged,
  onClose,
}: {
  schoolId: string;
  plans: readonly PlanRow[];
  lang: Lang;
  auth?: StaffAuth;
  onChanged: () => void;
  onClose: () => void;
}) {
  const t = BILLING_COPY[lang];
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [planId, setPlanId] = useState("");
  const [note, setNote] = useState("");

  const detail = useResource<SubscriptionDetail>(
    (signal) => getSubscription(schoolId, { ...auth, signal }),
    ["subscription", schoolId, String(version)]
  );

  const refresh = useCallback(() => {
    setVersion((n) => n + 1);
    onChanged();
  }, [onChanged]);

  const assignable = useMemo(() => plans.filter((p) => p.isActive), [plans]);

  /*
   * 🔴 THE TRIAL CONTROL, WHICH THIS PANEL DID NOT HAVE AND THE SERVER DID.
   *
   * `POST /superadmin/schools/:id/subscription/trial` has existed since F3 and
   * nothing in this app could reach it — so the only trials with an end date
   * were the ones schools gave themselves by signing up, and every trial an
   * operator granted was the open-ended kind `billing.dto.ts` calls *"a free
   * customer nobody decided to give away"*.
   *
   * ⚠️ A DATE INPUT, NOT A NUMBER OF DAYS, BECAUSE THIS IS THE EXTENDING CASE.
   * The onboarding wizard offers day presets because nobody has agreed to a date
   * yet; here the operator is on a call saying *"we will carry you to the end of
   * term"*, and a day count would make them do the arithmetic the DTO says the
   * screen should do.
   */
  const [trialEnd, setTrialEnd] = useState("");



  const run = useCallback(
    async (work: () => Promise<unknown>) => {
      setBusy(true);
      setFailed(false);
      try {
        await work();
        setNote("");
        refresh();
      } catch {
        setFailed(true);
      } finally {
        setBusy(false);
      }
    },
    [refresh]
  );

  /*
   * ⚠️ IT SENDS `planId` ONLY WHEN THE OPERATOR PICKED ONE. Omitted means *"the
   * plan they are already on"* — the DTO is explicit that a required plan here
   * is how extending a trial silently moves a school onto whatever the dropdown
   * happened to default to.
   */
  const extendTrial = useCallback(() => {
    if (trialEnd === "") return;
    void run(() =>
      grantTrial(
        schoolId,
        {
          trialEndsAt: new Date(`${trialEnd}T00:00:00.000Z`).toISOString(),
          ...(planId === "" ? {} : { planId }),
          note: note || null,
        },
        auth
      ).then((r) => {
        setTrialEnd("");
        return r;
      })
    );
  }, [trialEnd, planId, note, schoolId, auth, run]);

  return (
    <Panel>
      {/*
       * ⚠️ THE HEADER IS OUTSIDE `DataSurface`, WHICH THE SCHOOL NAME IS NOT.
       * The name has to wait for the fetch; the ✕ must not. A dialog whose only
       * visible exit appears after the request resolves is a dialog an operator
       * is stuck in for as long as the network is slow — and stuck in with no
       * way out at all if the request fails and the surface swaps to an error.
       */}
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-dash-lg font-extrabold text-ink">
          {detail.data?.schoolName ?? t.schoolDialog}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t.close}
          className="-mt-1 -mr-1 shrink-0 rounded-lg p-1 text-lg font-bold text-muted transition-colors hover:bg-canvas hover:text-ink"
        >
          ✕
        </button>
      </div>
      <DataSurface
        state={detail.state}
        skeletonRows={4}
        errorMessage={t.loadFailed}
        errorRetryLabel={t.retry}
        onRetry={detail.retry}
      >
        {detail.data === null ? null : (
          <div className="space-y-4 pt-3">
            <p className="flex flex-wrap items-center gap-2 text-dash-xs text-muted">
              <span>
                {t.seats}: <span className="tabular font-bold text-ink">{detail.data.seats}</span>
              </span>
              {detail.data.chargePaise !== null && (
                <span>
                  {t.charge}:{" "}
                  <span className="tabular font-bold text-ink">
                    {formatPaise(detail.data.chargePaise, detail.data.plan?.currency ?? "INR")}
                  </span>
                </span>
              )}
            </p>

            <div className="grid max-w-2xl gap-4 sm:grid-cols-2 pt-2">
              <Field label={t.plan} htmlFor="sub-plan" width="full">
                <Select id="sub-plan" value={planId} onChange={(e) => setPlanId(e.target.value)}>
                  <option value="">{t.pickPlan}</option>
                  {assignable.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} — {formatPaise(p.perStudentPaise, p.currency)}/{t.perStudent}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label={t.note} htmlFor="sub-note" hint={t.noteHint} width="full">
                <Input id="sub-note" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
              </Field>
            </div>
            <div className="flex flex-wrap items-end gap-4">
              <DashButton
                disabled={busy || planId === ""}
                onClick={() => void run(() => startSubscription(schoolId, { planId, note: note || null }, auth))}
              >
                {detail.data.plan === null ? t.startPlan : t.movePlan}
              </DashButton>
            </div>

            {/* 🔴 A FREE TRIAL, WITH AN END DATE — the route that had no screen.
                A trial with no end is not a trial; it is a free customer nobody
                decided to give away, found a year later on a list. */}
            <div className="pt-4 border-t border-line">
              <h3 className="text-dash-md font-extrabold text-ink">Free trial</h3>
              <p className="text-dash-xs text-muted mt-0.5">
                {detail.data.status === "TRIAL" && detail.data.trialEndsAt !== null
                  ? `Currently free until ${new Date(detail.data.trialEndsAt).toLocaleDateString()}.`
                  : detail.data.status === "TRIAL"
                    ? "On a trial with no end date. Give it one."
                    : "Puts the school on a trial of the plan above, or the one it is already on."}
              </p>
              <div className="mt-3 flex flex-wrap items-end gap-4">
                <Field label="Free until" htmlFor="sub-trial-end">
                  <Input
                    id="sub-trial-end"
                    type="date"
                    value={trialEnd}
                    onChange={(e) => setTrialEnd(e.target.value)}
                  />
                </Field>
                <DashButton disabled={busy || trialEnd === ""} onClick={extendTrial}>
                  {detail.data.status === "TRIAL" ? "Change the trial" : "Start a free trial"}
                </DashButton>
              </div>
            </div>
            {/* 🔴 `failed` WAS SET AND NEVER RENDERED. A move that did not happen
                looked exactly like one that did — the panel simply stopped. */}
            {failed ? (
              <Note tone="danger" icon={<AlertIcon size={16} />}>
                {t.actionFailed}
              </Note>
            ) : null}
          </div>
        )}
      </DataSurface>
    </Panel>
  );
}

function PlanCatalogue({
  lang,
  auth,
  plans,
}: {
  lang: Lang;
  auth?: StaffAuth;
  plans: ReturnType<typeof useResource<PlanRow[]>>;
}) {
  const t = BILLING_COPY[lang];
  const [draft, setDraft] = useState<PlanDraft>({
    code: "",
    name: "",
    perStudent: "",
    floor: "",
    blurb: "",
  });
  const [period, setPeriod] = useState<BillingPeriod>("MONTHLY");
  const [isPublic, setIsPublic] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);
  /*
   * 🔴 THE FORM IS A DIALOG NOW, AND THE TAB IS THE CATALOGUE IT WRITES TO.
   * Six fields and a checkbox WERE the whole Plans tab: an operator opened it to
   * check what "school-annual" charges and got a blank form instead of an
   * answer, with `noPlans`' own "create the first one above" pointing at a list
   * that was never drawn. The rates are what the tab is for; making one is an
   * action taken against it.
   */
  const [creating, setCreating] = useState(false);
  const closeForm = useCallback(() => setCreating(false), []);
  /*
   * 🔴 ERRORS APPEAR WHEN A FIELD HAS BEEN LEFT, NOT AS IT IS TYPED. "A plan
   * needs a code" under an empty box the operator has not reached yet is noise;
   * the same sentence after they tab past it is the point. Submitting touches
   * everything at once, so a blind press still says which field is wrong.
   */
  const [touched, setTouched] = useState<Record<keyof PlanDraft, boolean>>({
    code: false,
    name: false,
    perStudent: false,
    floor: false,
    blurb: false,
  });

  const errors = validatePlanDraft(draft);
  const valid = planDraftIsValid(draft);
  const remark = planFloorRemark(draft);
  const show = (field: keyof PlanDraft) => (touched[field] ? errors[field] : null);
  const set = (field: keyof PlanDraft) => (value: string) => {
    setDraft((d) => ({ ...d, [field]: value }));
    setCreated(null);
    setFailed(null);
  };
  const leave = (field: keyof PlanDraft) => () => {
    setTouched((was) => ({ ...was, [field]: true }));
  };

  const submit = async () => {
    setTouched({ code: true, name: true, perStudent: true, floor: true, blurb: true });
    /* 🔴 THE GUARD THAT WAS MISSING. The old form disabled its button on an
       empty code or name and on nothing else, so an empty rate was sent, refused
       with a 400, and reported as a duplicate code. */
    if (!planDraftIsValid(draft)) return;

    setBusy(true);
    setFailed(null);
    setCreated(null);
    try {
      const row = await createPlan(
        {
          code: draft.code.trim(),
          name: draft.name.trim(),
          perStudentPaise: draft.perStudent.trim(),
          floorPaise: draft.floor.trim(),
          period,
          isPublic,
          /* Empty is "no description", which the server takes as null. */
          blurb: draft.blurb.trim() === "" ? null : draft.blurb.trim(),
        },
        auth
      );
      setCreated(row.name);
      /*
       * ⚠️ THE DIALOG CLOSES ON SUCCESS AND THE CONFIRMATION IS LEFT ON THE TAB
       * — beside the row that now exists. A success note inside a dialog that
       * stays open reads as "and here is the form again", which is how the same
       * plan gets created twice under two codes.
       */
      setCreating(false);
      setDraft({ code: "", name: "", perStudent: "", floor: "", blurb: "" });
      setTouched({ code: false, name: false, perStudent: false, floor: false, blurb: false });
      setPeriod("MONTHLY");
      setIsPublic(false);
      plans.retry();
    } catch (err) {
      /* The status, never the server's sentence — see `client.ts`. */
      setFailed(describePlanFailure(err instanceof ApiError ? err.status : -1));
    } finally {
      setBusy(false);
    }
  };

  const rows = plans.data ?? [];

  return (
    <>
      <Panel>
        <SectionHead
          count={rows.length}
          hint={t.catalogueHint}
          actions={
            <DashButton onClick={() => setCreating(true)}>{t.newPlan}</DashButton>
          }
        >
          {t.catalogue}
        </SectionHead>

        {/* 🔴 READ-ONLY, AND THE MISSING CONTROLS ARE NAMED RATHER THAN DRAWN.
            `publish`, `unlist`, `retire` and `restore` all have copy in
            `billingCopy` and a route behind `updatePlan`, and none of them has a
            button here. A row of buttons that half-works is worse than a list
            that plainly reads; whoever adds them should add all four. */}
        <div className="mt-4">
          <DataSurface
            state={plans.state}
            skeletonRows={3}
            emptyMessage={t.noPlans}
            errorMessage={t.loadFailed}
            errorRetryLabel={t.retry}
            onRetry={plans.retry}
          >
            <TableCard label={t.catalogue}>
              <table className="w-full border-collapse">
                <Thead
                  headers={[
                    t.planName,
                    t.planCode,
                    t.perStudentPrice,
                    t.billingPeriod,
                    t.floorPrice,
                    t.visibility,
                  ]}
                />
                <tbody>
                  {rows.map((p) => (
                    <Tr key={p.id}>
                      <Td>{p.name}</Td>
                      <Td soft>{p.code}</Td>
                      <Td num>{formatPaise(p.perStudentPaise, p.currency)}</Td>
                      <Td soft>{t.periodWord[p.period]}</Td>
                      <Td num>{formatPaise(p.floorPaise, p.currency)}</Td>
                      <Td>
                        {/* ⚠️ TWO INDEPENDENT FACTS, AND NEITHER IMPLIES THE
                            OTHER — a retired plan can still be flagged public,
                            and a live plan is usually not listed. Only the first
                            is a status, so only the first is a pill; a second
                            pill beside it would read as an alternative to it. */}
                        <span className="flex flex-col items-start gap-1">
                          <StatusPill
                            tone={p.isActive ? "success" : "neutral"}
                            icon={toneIcon(p.isActive ? "success" : "neutral")}
                          >
                            {p.isActive ? t.live : t.retired}
                          </StatusPill>
                          <span className="font-dash text-dash-xs text-muted">
                            {p.isPublic ? t.published : t.unlisted}
                          </span>
                        </span>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </table>
            </TableCard>
          </DataSurface>
        </div>

        {created ? (
          <div className="mt-4">
            <Note tone="success" icon={<CheckIcon size={16} />}>
              {`“${created}” is in the catalogue.`}
            </Note>
          </div>
        ) : null}
      </Panel>

      {creating && (
        <ModalCard label={t.newPlan} onClose={closeForm}>
          <Panel>
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <div className="flex items-start justify-between gap-3">
                <h2 className="font-dash text-dash-md font-extrabold text-ink">{t.newPlan}</h2>
                <button
                  type="button"
                  onClick={closeForm}
                  aria-label={t.close}
                  className="-mt-1 -mr-1 shrink-0 rounded-lg p-1 text-lg font-bold text-muted transition-colors hover:bg-canvas hover:text-ink"
                >
                  ✕
                </button>
              </div>
              <p className="mt-1 max-w-[70ch] font-dash text-dash-xs text-muted">{t.catalogueHint}</p>

              <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                <Field label={t.planCode} hint={show("code") ?? t.planCodeHint} htmlFor="plan-code" width="full">
                  <Input
                    id="plan-code"
                    value={draft.code}
                    maxLength={PLAN_CODE_MAX}
                    placeholder="school-annual"
                    aria-invalid={show("code") !== null}
                    onBlur={leave("code")}
                    onChange={(e) => {
                      set("code")(e.target.value);
                    }}
                  />
                </Field>

                <Field label={t.planName} hint={show("name")} htmlFor="plan-name" width="full">
                  <Input
                    id="plan-name"
                    value={draft.name}
                    maxLength={PLAN_NAME_MAX}
                    placeholder="School Annual"
                    aria-invalid={show("name") !== null}
                    onBlur={leave("name")}
                    onChange={(e) => {
                      set("name")(e.target.value);
                    }}
                  />
                </Field>

                <Field label={t.billingPeriod} hint={t.periodHint} htmlFor="plan-period" width="full">
                  <Select
                    id="plan-period"
                    value={period}
                    onChange={(e) => {
                      setPeriod(e.target.value as BillingPeriod);
                    }}
                  >
                    <option value="MONTHLY">{t.periodMonthly}</option>
                    <option value="QUARTERLY">{t.periodQuarterly}</option>
                    <option value="YEARLY">{t.periodYearly}</option>
                  </Select>
                </Field>

                <Field
                  label={t.perStudentPrice}
                  hint={show("perStudent") ?? t.amountHint}
                  htmlFor="plan-rate"
                  width="full"
                >
                  <Input
                    id="plan-rate"
                    inputMode="decimal"
                    value={draft.perStudent}
                    placeholder="40.50"
                    aria-invalid={show("perStudent") !== null}
                    onBlur={leave("perStudent")}
                    onChange={(e) => {
                      set("perStudent")(e.target.value);
                    }}
                  />
                </Field>

                <Field label={t.floorPrice} hint={show("floor") ?? t.floorHint} htmlFor="plan-floor" width="full">
                  <Input
                    id="plan-floor"
                    inputMode="decimal"
                    value={draft.floor}
                    placeholder="5000"
                    aria-invalid={show("floor") !== null}
                    onBlur={leave("floor")}
                    onChange={(e) => {
                      set("floor")(e.target.value);
                    }}
                  />
                </Field>

                <Field label={t.planBlurb} hint={show("blurb") ?? t.blurbHint} htmlFor="plan-blurb" width="full">
                  <Input
                    id="plan-blurb"
                    value={draft.blurb}
                    maxLength={PLAN_BLURB_MAX}
                    aria-invalid={show("blurb") !== null}
                    onBlur={leave("blurb")}
                    onChange={(e) => {
                      set("blurb")(e.target.value);
                    }}
                  />
                </Field>
              </div>

              {/* 🔴 The arithmetic said out loud before the plan exists — a minimum
                  below one child's rate is legal and never applies, and an operator
                  who does not see that finds out from an invoice. */}
              {remark ? (
                <p className="mt-3 font-dash text-dash-xs text-muted">{remark}</p>
              ) : null}

              <div className="mt-3 max-w-xl">
                <CheckboxRow checked={isPublic} onChange={setIsPublic} hint={t.publishHint}>
                  {t.publishOnCreate}
                </CheckboxRow>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <DashButton type="submit" disabled={busy || !valid}>
                  {busy ? t.creating : t.createPlan}
                </DashButton>
              </div>

              {/* ⚠️ THE FAILURE STAYS IN THE DIALOG, unlike the success. The operator
                  has to fix a field and press again, and they cannot do either from a
                  note left behind on a tab. */}
              {failed ? (
                <div className="mt-4">
                  <Note tone="danger" icon={<AlertIcon size={16} />}>
                    {failed}
                  </Note>
                </div>
              ) : null}
            </form>
          </Panel>
        </ModalCard>
      )}
    </>
  );
}

function PaymentsPanel({ lang, auth }: { lang: Lang; auth?: StaffAuth }) {
  const t = BILLING_COPY[lang];
  const payments = useResource<PaymentRow[]>(
    (signal) => getPayments({ ...auth, signal }),
    ["payments"],
    { isEmpty: (rows) => rows.length === 0 }
  );

  return (
    <Panel>
      <SectionHead count={payments.data?.length} hint={t.paymentsHint}>
        {t.paymentsTitle}
      </SectionHead>
      <DataSurface
        state={payments.state}
        skeletonRows={4}
        emptyMessage={t.noPayments}
        errorMessage={t.loadFailed}
        onRetry={payments.retry}
      >
        <TableCard>
          <table className="w-full border-collapse">
            <Thead headers={[t.when, t.school, t.amount, t.how, t.source, t.paymentStatus, t.note]} />
            <tbody>
              {(payments.data ?? []).map((p) => (
                <Tr key={p.id}>
                  <Td soft>{new Date(p.paidAt ?? p.createdAt).toLocaleString()}</Td>
                  <Td bold>{p.schoolName}</Td>
                  <Td num>{formatPaise(p.amountPaise, p.currency)}</Td>
                  <Td soft>{p.method ?? "—"}</Td>
                  <Td soft>{p.source === "MANUAL" ? t.byHand : t.online}</Td>
                  <Td>
                    <StatusPill
                      tone={p.status === "PAID" ? "success" : "warning"}
                      icon={toneIcon(p.status === "PAID" ? "success" : "warning")}
                    >
                      {paymentStatusLabel(p.status, lang)}
                    </StatusPill>
                  </Td>
                  <Td soft>{p.note ?? "—"}</Td>
                </Tr>
              ))}
            </tbody>
          </table>
        </TableCard>
      </DataSurface>
    </Panel>
  );
}
